import { mediaFolderApi, mediaFileApi, mediaImageApi } from "./api";
import type {
  BulkDeleteResponse,
  FolderContents,
  MediaFile,
  MediaFolder,
  MediaImage,
  UploadFileResponse,
} from "../models/media";

export const getFolders = async (
  parentId?: number | null,
): Promise<MediaFolder[]> => {
  try {
    const response = await mediaFolderApi.getAll(
      parentId !== undefined ? { parent: parentId } : { root: true },
    );
    return response.data;
  } catch (_error) {
    throw new Error("Failed to fetch folders");
  }
};

export const createFolder = async (
  name: string,
  parentId?: number | null,
): Promise<MediaFolder> => {
  try {
    const payload = {
      name,
      ...(parentId !== undefined && { parent: { id: parentId } }),
    };

    const response = await mediaFolderApi.create(payload);
    return response.data;
  } catch (_error) {
    throw new Error("Failed to create folder");
  }
};

export const updateFolder = async (
  id: number,
  name: string,
): Promise<MediaFolder> => {
  try {
    const response = await mediaFolderApi.update(id, { name });
    return response.data;
  } catch (_error) {
    throw new Error("Failed to update folder");
  }
};

export const deleteFolder = async (id: number): Promise<void> => {
  try {
    await mediaFolderApi.delete(id);
  } catch (_error) {
    throw new Error("Failed to delete folder");
  }
};

export const getFolderContents = async (
  folderId?: number | null,
  params?: { page?: number; page_size?: number; type?: string; search?: string },
): Promise<FolderContents> => {
  try {
    let response;
    if (folderId !== undefined && folderId !== null) {
      response = await mediaFolderApi.getContents(folderId, params);
    } else {
      response = await mediaFolderApi.getRootContents(params);
    }
    return response.data;
  } catch (_error) {
    throw new Error("Failed to fetch folder contents");
  }
};

export const uploadFile = async (
  file: File,
  folderId?: number | null,
  metadata?: {
    name?: string;
    description?: string;
    default_alt_text?: string;
    default_caption?: string;
    author?: string;
  },
): Promise<UploadFileResponse> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    if (folderId !== undefined) {
      formData.append(
        "folder",
        folderId === null ? "null" : folderId.toString(),
      );
    }

    // Add metadata if provided
    if (metadata) {
      if (metadata.name) formData.append("name", metadata.name);
      if (metadata.description)
        formData.append("description", metadata.description);
      if (metadata.default_alt_text)
        formData.append("default_alt_text", metadata.default_alt_text);
      if (metadata.default_caption)
        formData.append("default_caption", metadata.default_caption);
      if (metadata.author) formData.append("author", metadata.author);
    }

    const response = await mediaFileApi.upload(formData);

    return {
      success: true,
      file: response.data,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.error || "Failed to upload file",
    };
  }
};

export const updateFile = async (
  id: number,
  data: { name?: string; description?: string; folder_id?: number | null },
): Promise<MediaFile | MediaImage> => {
  try {
    const payload: any = {};

    if (data.name !== undefined) payload.name = data.name;
    if (data.description !== undefined) payload.description = data.description;
    if (data.folder_id !== undefined) payload.folder = { id: data.folder_id };

    const response = await mediaFileApi.update(id, payload);
    return response.data;
  } catch (_error) {
    throw new Error("Failed to update file");
  }
};

export const deleteFile = async (id: number): Promise<void> => {
  try {
    await mediaFileApi.delete(id);
  } catch (_error) {
    throw new Error("Failed to delete file");
  }
};

export const bulkDeleteFiles = async (
  ids: number[],
): Promise<BulkDeleteResponse> => {
  try {
    const response = await mediaFileApi.bulkDelete(ids);
    return response.data;
  } catch {
    throw new Error("Failed to delete files");
  }
};

export const getImages = async (
  folderId?: number | null,
): Promise<MediaImage[]> => {
  try {
    const response = await mediaImageApi.getAll(
      folderId !== undefined ? { folder: folderId } : undefined,
    );
    return response.data;
  } catch (_error) {
    throw new Error("Failed to fetch images");
  }
};

export const updateImage = async (
  id: number,
  data: {
    name?: string;
    description?: string;
    alt_text?: string;
    caption?: string;
    folder_id?: number | null;
  },
): Promise<MediaImage> => {
  try {
    const payload: any = {};

    if (data.name !== undefined) payload.name = data.name;
    if (data.description !== undefined) payload.description = data.description;
    if (data.alt_text !== undefined) payload.default_alt_text = data.alt_text;
    if (data.caption !== undefined) payload.default_caption = data.caption;
    if (data.folder_id !== undefined) payload.folder = { id: data.folder_id };

    const response = await mediaImageApi.update(id, payload);
    return response.data;
  } catch (_error) {
    throw new Error("Failed to update image");
  }
};

export const getFolderPath = async (
  folderId: number,
): Promise<MediaFolder[]> => {
  try {
    const response = await mediaFolderApi.getPath(folderId);
    return response.data;
  } catch (error) {
    console.error("Error fetching folder path:", error);
    // If the API endpoint doesn't exist yet, build path manually
    try {
      // Plan B: Try to build path manually by getting folders one by one
      const path: MediaFolder[] = [];
      let currentId = folderId;

      // Maximum depth to prevent infinite loops
      let maxDepth = 10;

      while (currentId && maxDepth > 0) {
        const folderResponse = await mediaFolderApi.getById(currentId);
        const folder = folderResponse.data;
        path.unshift(folder); // Add to beginning of array

        if (!folder.parent_id) {
          break;
        }

        currentId = folder.parent_id;
        maxDepth--;
      }

      return path;
    } catch (fallbackError) {
      console.error("Failed to build folder path manually:", fallbackError);
      return [];
    }
  }
};
